import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { MatDialog } from '@angular/material/dialog';
import {
  GridDataResult,
  DataStateChangeEvent,
  GridModule
} from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import { DatePipe, NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ServiceStatus } from '../shared/service-status.enum';
import { ImpersonationLogsService } from './impersonation-logs.service';
import { ImpersonationLogDetailComponent } from './impersonation-log-detail/impersonation-log-detail.component';
import {
  ImpersonationAuditLog,
  ImpersonationAuditQueryParams,
  ImpersonationAuditUser
} from './models/impersonation-audit.model';

@Component({
  selector: 'app-impersonation-logs',
  standalone: true,
  templateUrl: './impersonation-logs.component.html',
  styleUrl: './impersonation-logs.component.scss',
  imports: [GridModule, DatePipe, FormsModule, NgClass]
})
export class ImpersonationLogsComponent implements OnInit, OnDestroy {
  public readonly PAGE_SIZE = 50;
  public ServiceStatus = ServiceStatus;
  public loadStatus: ServiceStatus = ServiceStatus.unset;

  public impersonators: ImpersonationAuditUser[] = [];
  public impersonatedUsers: ImpersonationAuditUser[] = [];

  public selectedRealUserId: number | null = null;
  public selectedImpersonatedUserId: number | null = null;

  public gridData: GridDataResult = { data: [], total: 0 };

  public gridState: State = {
    skip: 0,
    take: this.PAGE_SIZE,
    sort: [{ field: 'CreatedDateUTC', dir: 'desc' }]
  };

  private subscription?: Subscription;

  constructor(
    private impersonationLogsService: ImpersonationLogsService,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    this.loadParticipants();
    this.loadLogs();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  onStateChange(state: DataStateChangeEvent): void {
    this.gridState = state;
    this.loadLogs();
  }

  onFilterChange(): void {
    this.gridState.skip = 0;
    this.loadLogs();
  }

  clearFilters(): void {
    this.selectedRealUserId = null;
    this.selectedImpersonatedUserId = null;
    this.onFilterChange();
  }

  refresh(): void {
    this.loadParticipants();
    this.loadLogs();
  }

  openDetail(log: ImpersonationAuditLog): void {
    const isMobile = window.innerWidth <= 768;
    this.dialog.open(ImpersonationLogDetailComponent, {
      width: isMobile ? '100vw' : '700px',
      maxWidth: isMobile ? '100vw' : '90vw',
      maxHeight: isMobile ? '100vh' : '90vh',
      height: isMobile ? '100vh' : 'auto',
      panelClass: isMobile ? 'fullscreen-mobile-dialog' : '',
      data: log
    });
  }

  getStatusClass(statusCode: number): string {
    if (statusCode >= 500) return 'status-error';
    if (statusCode >= 400) return 'status-warning';
    return 'status-ok';
  }

  private loadParticipants(): void {
    this.impersonationLogsService.getParticipants().subscribe({
      next: result => {
        this.impersonators = result.Impersonators;
        this.impersonatedUsers = result.ImpersonatedUsers;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Failed to load impersonation participants', err);
      }
    });
  }

  private loadLogs(): void {
    this.loadStatus = ServiceStatus.loading;
    this.subscription?.unsubscribe();

    const query = this.buildQuery();

    this.subscription = this.impersonationLogsService.getLogs(query).subscribe({
      next: result => {
        this.gridData = { data: result.Results, total: result.TotalCount };
        this.loadStatus = ServiceStatus.loaded;
      },
      error: (err: HttpErrorResponse) => {
        this.loadStatus = ServiceStatus.error;
        console.error('Failed to load impersonation logs', err);
      }
    });
  }

  private buildQuery(): ImpersonationAuditQueryParams {
    const query: ImpersonationAuditQueryParams = {
      page: Math.floor((this.gridState.skip ?? 0) / this.PAGE_SIZE) + 1,
      pageSize: this.PAGE_SIZE
    };

    const sort = this.gridState.sort?.[0];
    if (sort && sort.dir) {
      query.sortField = sort.field;
      query.sortDir = sort.dir;
    }

    if (this.selectedRealUserId != null) {
      query.realUserId = this.selectedRealUserId;
    }
    if (this.selectedImpersonatedUserId != null) {
      query.impersonatedUserId = this.selectedImpersonatedUserId;
    }

    return query;
  }
}
